import React, { useState } from "react";
import { ToggleRight, Loader2, AlertTriangle } from "lucide-react";
import { Modal, FieldLabel, inputCls, InlineMsg } from "../apikeys/shared";

const MIN_REASON = 10;

// change: { kind: "toggle", mod, visible } | { kind: "preset", hide: [labels], show: [labels] }
export default function ConfirmChangeModal({ change, labelOf, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ok = reason.trim().length >= MIN_REASON;
  const preset = change.kind === "preset";
  const title = preset ? "Apply pilot module set" : `${change.visible ? "Show" : "Hide"} ${change.mod.label}`;

  async function submit(e) {
    e.preventDefault();
    if (!ok || busy) return;
    setBusy(true); setError(null);
    try { await onConfirm(reason.trim()); onClose(); } catch (err) { setError(err.message); setBusy(false); }
  }

  return (
    <Modal title={title} subtitle="Applies to every user and role immediately" icon={ToggleRight}
           onClose={onClose} busy={busy} testid="module-confirm-modal">
      <form onSubmit={submit} className="p-5 space-y-3.5">
        {preset ? <PresetImpact change={change} /> : <ToggleImpact change={change} labelOf={labelOf} />}
        <FieldLabel label="Reason for this change (required)" hint={`${reason.trim().length}/${MIN_REASON} min`}>
          <textarea data-testid="module-confirm-reason" rows={3} value={reason} disabled={busy} autoFocus
                    onChange={e => setReason(e.target.value)} maxLength={500}
                    placeholder="e.g. Pilot scope limited to inventory and demand per steering committee."
                    className={`${inputCls} font-sans resize-none`} />
        </FieldLabel>
        {error && <InlineMsg testid="module-confirm-error">{error}</InlineMsg>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy} data-testid="module-confirm-cancel"
                  className="px-3 py-1.5 text-[11.5px] border border-border rounded-sm hover:bg-surface-2 disabled:opacity-40">Cancel</button>
          <button type="submit" disabled={!ok || busy} data-testid="module-confirm-submit"
                  className="px-4 py-1.5 text-[11.5px] font-semibold bg-primary text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
            {busy && <Loader2 size={11} className="animate-spin" />}
            {busy ? "Applying…" : "Confirm change"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ToggleImpact({ change, labelOf }) {
  const { mod, visible } = change;
  return (
    <div className="text-[11.5px] border border-border bg-surface-2/50 rounded-sm p-3 space-y-1.5 leading-snug" data-testid="module-confirm-impact">
      <div className="flex items-center gap-1.5 font-semibold text-warning"><AlertTriangle size={12} /> What this affects</div>
      <div>{mod.sensitivity}</div>
      <div className="text-muted">{mod.dependencies}</div>
      {!visible && mod.linked_from.length > 0 && (
        <div className="text-muted">Links to it from {mod.linked_from.map(labelOf).join(", ")} will be removed or redirected.</div>
      )}
      <div className="text-muted">{visible ? "It will reappear in the sidebar for every role that is allowed to see it." : "It disappears from the sidebar for everyone; direct links show a “paused for the pilot phase” page."}</div>
    </div>
  );
}

function PresetImpact({ change }) {
  return (
    <div className="text-[11.5px] border border-border bg-surface-2/50 rounded-sm p-3 space-y-2 leading-snug" data-testid="module-confirm-impact">
      <div className="text-muted">Restores the inventory &amp; demand pilot set. Changes from the current state:</div>
      {change.hide.length > 0 && <div><strong className="text-critical">Hide:</strong> {change.hide.join(", ")}</div>}
      {change.show.length > 0 && <div><strong className="text-success">Show:</strong> {change.show.join(", ")}</div>}
    </div>
  );
}
